import { describeUnknown } from './errors';
import { useSession } from './session';

export class ApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly code: string,
    public readonly details?: unknown,
    /** Translatable message key and its values (see lib/errors). */
    public readonly key?: string,
    public readonly params?: Record<string, string | number>,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  form?: FormData;
  /** Send the current workspace id (default true). */
  workspace?: boolean;
  signal?: AbortSignal;
}

export async function api<T = any>(path: string, opts: RequestOptions = {}): Promise<T> {
  const headers: Record<string, string> = {};
  if (opts.workspace !== false) {
    const ws = useSession.getState().workspaceId;
    if (ws) headers['x-workspace-id'] = ws;
  }
  let body: BodyInit | undefined;
  if (opts.form) body = opts.form;
  else if (opts.body !== undefined) {
    headers['content-type'] = 'application/json';
    body = JSON.stringify(opts.body);
  }
  const res = await fetch(path, {
    method: opts.method ?? (body ? 'POST' : 'GET'),
    headers,
    body,
    credentials: 'same-origin',
    signal: opts.signal,
  });
  const text = await res.text();
  let data: any = null;
  if (text) {
    try {
      data = JSON.parse(text);
    } catch {
      data = text;
    }
  }
  if (!res.ok) {
    const err = data?.error;
    throw new ApiError(
      err?.message ?? `Request failed (${res.status})`,
      res.status,
      err?.code ?? 'error',
      err?.details,
      err?.key,
      err?.params,
    );
  }
  return data as T;
}

export function uploadFile<T = any>(
  path: string,
  file: File,
  fields: Record<string, string> = {},
): Promise<T> {
  const form = new FormData();
  for (const [k, v] of Object.entries(fields)) form.append(k, v);
  form.append('file', file);
  return api<T>(path, { method: 'POST', form });
}

/** Translated, human-readable text for any error thrown by `api()`. */
export function errorMessage(err: unknown): string {
  return describeUnknown(err);
}

/**
 * POST that answers with JSON lines as they come (the AI director's steps). Errors before the
 * stream starts are thrown like `api()` errors.
 */
export async function apiStream(
  path: string,
  body: unknown,
  onLine: (line: any) => void,
  signal?: AbortSignal,
): Promise<void> {
  const headers: Record<string, string> = { 'content-type': 'application/json' };
  const ws = useSession.getState().workspaceId;
  if (ws) headers['x-workspace-id'] = ws;
  const res = await fetch(path, {
    method: 'POST',
    headers,
    body: JSON.stringify(body),
    credentials: 'same-origin',
    signal,
  });
  if (!res.ok || !res.body) {
    const data = await res.json().catch(() => null);
    const err = data?.error;
    throw new ApiError(
      err?.message ?? `Request failed (${res.status})`,
      res.status,
      err?.code ?? 'error',
      err?.details,
      err?.key,
      err?.params,
    );
  }
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buf = '';
  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    buf += decoder.decode(value, { stream: true });
    let i: number;
    while ((i = buf.indexOf('\n')) >= 0) {
      const line = buf.slice(0, i).trim();
      buf = buf.slice(i + 1);
      if (line) onLine(JSON.parse(line));
    }
  }
  if (buf.trim()) onLine(JSON.parse(buf));
}
